import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Mail, RefreshCw, CheckCircle2, XCircle, Calendar, Filter, Inbox, Send, Loader2 } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL;

const formatTs = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const STATUS_PILL = {
  success: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  failed: 'bg-rose-100 text-rose-700 border-rose-200',
};

const SOURCE_LABEL = {
  scheduler: { label: 'Scheduled', cls: 'bg-violet-100 text-violet-700' },
  dashboard: { label: 'Dashboard', cls: 'bg-sky-100 text-sky-700' },
};

const TYPE_OPTIONS = [
  { value: 'all', label: 'All types' },
  { value: 'document_expiry', label: 'Document expiry' },
  { value: 'probation_confirmation', label: 'Probation confirmation' },
  { value: 'leave_request', label: 'Leave request (to manager)' },
  { value: 'leave_approval_request', label: 'Leave approval request' },
  { value: 'leave_approval_reminder', label: 'Leave approval reminder' },
  { value: 'leave_approval_decision', label: 'Leave approval decision (to HR)' },
  { value: 'leave_decision_employee', label: 'Leave decision (to employee)' },
  { value: 'welcome_back', label: 'Welcome back nudge' },
];

const STATUS_OPTIONS = [
  { value: 'all', label: 'All statuses' },
  { value: 'success', label: 'Sent' },
  { value: 'failed', label: 'Failed' },
];

const EmailLog = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [resending, setResending] = useState(null); // log_id currently resending
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const handleResend = async (row) => {
    if (!row.log_id) {
      toast({
        title: 'Cannot resend',
        description: 'This is a legacy entry without a stable ID. New emails are fully resendable.',
        variant: 'destructive',
      });
      return;
    }
    if (!window.confirm(
      `Resend this email?\n\nTo: ${row.recipient}\nSubject: ${row.subject}`
    )) return;
    setResending(row.log_id);
    try {
      const r = await axios.post(`${API}/api/admin/email-logs/${row.log_id}/resend`, {}, { headers });
      toast({
        title: 'Email resent',
        description: r.data?.message || `Sent to ${row.recipient}.`,
      });
      // Re-fetch so the new attempt appears at the top of the log.
      await load();
    } catch (err) {
      toast({
        title: 'Resend failed',
        description: err?.response?.data?.detail || err?.message || 'Please retry shortly.',
        variant: 'destructive',
      });
    } finally {
      setResending(null);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/admin/email-logs?days=${days}&limit=500`, { headers });
      setRows(r.data?.rows || []);
    } catch (err) {
      toast({
        title: 'Could not load email log',
        description: err?.response?.data?.detail || err?.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  const filtered = rows.filter(r => (
    (typeFilter === 'all' || r.type === typeFilter) &&
    (statusFilter === 'all' || r.status === statusFilter)
  ));

  const totals = {
    all: rows.length,
    sent: rows.filter(r => r.status === 'success').length,
    failed: rows.filter(r => r.status === 'failed').length,
  };

  return (
    <div className="p-6 space-y-4 max-w-7xl mx-auto" data-testid="email-log-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Mail className="h-7 w-7 text-indigo-600" /> Email Audit Log
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Every email this app has attempted to send — scheduled jobs + dashboard actions.
          </p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="email-log-refresh">
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {/* Stat tiles */}
      <div className="grid grid-cols-3 gap-3">
        <Card className="border border-gray-200 shadow-none">
          <CardContent className="p-4 flex items-center gap-3">
            <Inbox className="h-6 w-6 text-gray-400" />
            <div>
              <p className="text-[11px] uppercase tracking-wider text-gray-400">Total</p>
              <p className="text-xl font-bold">{totals.all}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-emerald-200 bg-emerald-50/40 shadow-none">
          <CardContent className="p-4 flex items-center gap-3">
            <CheckCircle2 className="h-6 w-6 text-emerald-500" />
            <div>
              <p className="text-[11px] uppercase tracking-wider text-emerald-700">Sent</p>
              <p className="text-xl font-bold text-emerald-700">{totals.sent}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-rose-200 bg-rose-50/40 shadow-none">
          <CardContent className="p-4 flex items-center gap-3">
            <XCircle className="h-6 w-6 text-rose-500" />
            <div>
              <p className="text-[11px] uppercase tracking-wider text-rose-700">Failed</p>
              <p className="text-xl font-bold text-rose-700">{totals.failed}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-3 flex flex-wrap items-center gap-2">
          <Filter className="h-4 w-4 text-gray-400" />
          <label className="text-xs text-gray-500 flex items-center gap-1.5">
            <Calendar className="h-3.5 w-3.5" /> Range
            <select
              className="text-xs border rounded px-2 py-1"
              value={days}
              onChange={(e) => setDays(parseInt(e.target.value, 10))}
              data-testid="email-log-days"
            >
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
              <option value={365}>Last 12 months</option>
            </select>
          </label>
          <label className="text-xs text-gray-500 flex items-center gap-1.5">
            Type
            <select
              className="text-xs border rounded px-2 py-1"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              data-testid="email-log-type"
            >
              {TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </label>
          <label className="text-xs text-gray-500 flex items-center gap-1.5">
            Status
            <select
              className="text-xs border rounded px-2 py-1"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              data-testid="email-log-status"
            >
              {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </label>
          <span className="text-xs text-gray-400 ml-auto">Showing {filtered.length} of {rows.length}</span>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold text-gray-800">Email history</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-sm text-gray-400 py-8 text-center">Loading…</p>
          ) : filtered.length === 0 ? (
            <p className="text-sm text-gray-400 py-8 text-center" data-testid="email-log-empty">
              No emails match the current filters.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-[11px] uppercase tracking-wider text-gray-400 border-b">
                  <tr>
                    <th className="text-left py-2 pr-3">When</th>
                    <th className="text-left py-2 pr-3">Recipient</th>
                    <th className="text-left py-2 pr-3">Subject</th>
                    <th className="text-left py-2 pr-3">Source</th>
                    <th className="text-left py-2 pr-3">Status</th>
                    <th className="text-left py-2 pr-3 w-[100px]">Resend</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r, i) => {
                    const src = SOURCE_LABEL[r.source] || { label: r.source, cls: 'bg-gray-100 text-gray-600' };
                    return (
                      <tr key={i} className="border-b last:border-0 hover:bg-gray-50" data-testid={`email-log-row-${i}`}>
                        <td className="py-2 pr-3 text-gray-600 whitespace-nowrap">{formatTs(r.timestamp)}</td>
                        <td className="py-2 pr-3 text-gray-800 font-mono text-[12px]">{r.recipient}</td>
                        <td className="py-2 pr-3 text-gray-800">
                          <div>{r.subject}</div>
                          {r.error && (
                            <div className="text-[11px] text-rose-600 mt-0.5 truncate max-w-[420px]" title={r.error}>
                              Error: {r.error}
                            </div>
                          )}
                        </td>
                        <td className="py-2 pr-3">
                          <Badge className={`text-[10px] ${src.cls}`}>{src.label}</Badge>
                        </td>
                        <td className="py-2 pr-3">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border ${STATUS_PILL[r.status] || ''}`}>
                            {r.status === 'success'
                              ? <CheckCircle2 className="h-3 w-3" />
                              : <XCircle className="h-3 w-3" />}
                            {r.status === 'success' ? 'Sent' : 'Failed'}
                          </span>
                        </td>
                        <td className="py-2 pr-3">
                          <button
                            type="button"
                            onClick={() => handleResend(r)}
                            disabled={resending === r.log_id || !r.log_id}
                            title={r.log_id ? 'Resend this email' : 'Legacy entry — no resend available'}
                            className="inline-flex items-center gap-1 px-2 py-1 text-[11px] rounded border bg-white text-indigo-700 border-indigo-200 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed"
                            data-testid={`email-resend-${i}`}
                          >
                            {resending === r.log_id
                              ? <Loader2 className="h-3 w-3 animate-spin" />
                              : <Send className="h-3 w-3" />}
                            Resend
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default EmailLog;
